import Middleman from '~/js/lib/middleman';
import {
  ProductSearchBuilder,
  DataValueFactory,
} from '@relewise/client';

/**
 * General DOCS overview
 * @filters https://github.com/Relewise/relewise-sdk-javascript/blob/f8b3cc234ef10d27cedcd70caa7649cefb6efe6e/lib/src/builders/filterBuilder.ts
 */

class Collection {
  constructor() {
    this.middleman = new Middleman();
  }

  /**
   * Makes a product search request build ready
   * @param {String} collectionId
   * @param {Number} limit number of results to return
   * @param {String} location
   * @param {Number} page
   * @param {string[]} facetGroups
   * @param {{ key: string, value: string }[]} queries
   * @param {String} defaultSort
   * @returns ProductSearchRequest
   * @docs https://github.com/Relewise/relewise-sdk-javascript/blob/3305669b3e895fdc5082006484dc3c5d112a0d09/lib/src/builders/search/productSearchBuilder.ts
   */
  getProductsAndFilters({
    collectionId,
    limit = 8,
    location,
    page = 1,
    facetGroups = [],
    queries = [],
    defaultSort = 'relevance',
  }) {
    const groupedQueries = queries.reduce((acc, obj) => {
      const existingGroup = acc.find((group) => group.key === obj.key);

      if (existingGroup) {
        existingGroup.values.push(obj.value);
      } else {
        acc.push({ key: obj.key, values: [obj.value] });
      }

      return acc;
    }, []);

    const uniqueFacetGroups = [...new Set(facetGroups)];

    const search = new ProductSearchBuilder({
      ...this.middleman.settings,
      ...{ displayedAtLocation: location },
    })

      /**
       * Pick which Product props to return.
       * Data can be found in my.relewise.com > Entities > Products
       */
      .setSelectedProductProperties({
        displayName: true,
        pricing: true,
        AllVariants: true,
        dataKeys: [
          'Vendor',
          `${window.Shopify.shop}_CreatedAt`,
          `${window.Shopify.shop}_ShopifyHandle`,
          `${window.Shopify.shop}_ShopifyProductId`,
          'Tags',
          'IsAvailable',
          'InStock',
        ],
      })

      /**
       * Fixes so the same product won't show n(amount of variants) times when
       * ...searching for an item id
       */
      .setExplodedVariants(1)

      /**
       * Pick which Product props to return.
       * Data can be found in my.relewise.com > Entities > Products
       */
      .setSelectedVariantProperties({
        displayName: true,
        pricing: true,
        dataKeys: [
          `${window.Shopify.shop}_ImageUrls`,
          `${window.Shopify.shop}_ShopifyVariantId`,
          `${window.Shopify.shop}_Size`,
          `${window.Shopify.shop}_HasInventory`,
          'InventoryQuantity',
          'IsAvailable',
        ],
      })

      /**
       * Implement retail media
       */
      .setRetailMedia({
        location: {
          key: 'COLLECTION_PAGE',
          placements: [{ key: 'TOP' }],
          variation: { key: window.matchMedia('(min-width: 1024px)').matches ? 'DESKTOP' : 'MOBILE' },
        },
      })

      /**
       * Set limit and offset for pagination
       */
      .pagination((paginate) => (
        paginate.setPageSize(limit)
          .setPage(page)
      ))

      .filters((filter) => (

        /**
         * Only return products from current shop/domain
         */
        filter.addProductDataFilter('ShopifyDomains', (condition) => (
          condition.addContainsCondition(DataValueFactory.string(window.Shopify.shop))
        ))

          /**
           * Only return variants from current shop/domain
           */
          .addVariantDataFilter('ShopifyDomains', (condition) => (
            condition.addContainsCondition(DataValueFactory.string(window.Shopify.shop))
          ))

          /**
           * Remove products marked as "Auktion". Second param is "negated"
           */
          .addProductDataFilter('Vendor', (condition) => condition.addEqualsCondition('Auktion', true))

          .addProductCategoryIdFilter('ImmediateParent', [collectionId])
      ))

      .facets((facet) => {
        const selectedStoreValues = groupedQueries.find((query) => query.key === `${window.Shopify.shop}_HasInventory`)?.values || [];

        facet.addProductDataStringValueFacet(`${window.Shopify.shop}_HasInventory`, 'Variant', [...selectedStoreValues], 'Or', { includeZeroHitsInAvailable: true });

        if (facetGroups.length) {
          uniqueFacetGroups.forEach((group) => {
            const selectedGroupValues = groupedQueries.find((query) => query.key === group)?.values || [];
            const priceQuery = queries.find((query) => query.key === 'SalesPrice');
            const saleQuery = queries.find((query) => query.key === 'IsOnSale');

            switch (group) {
              case 'SHOP_Size':
                facet.addProductDataStringValueFacet(group.replace('SHOP', window.Shopify.shop), 'Variant', [...selectedGroupValues], 'Or', { includeZeroHitsInAvailable: true });
                if (selectedGroupValues.length) {
                  facet.addProductDataBooleanValueFacet('IsAvailable', 'Variant', [true]);
                }
                break;

              case 'Brand':
                facet.addBrandFacet([...selectedGroupValues], { includeZeroHitsInAvailable: true });
                break;

              case 'ProductType':
                facet.addProductDataStringValueFacet('ProductType', 'Product', [...selectedGroupValues], undefined, { includeZeroHitsInAvailable: true });
                break;

              case 'SalesPrice':
                if (priceQuery) {
                  const min = parseInt(priceQuery.value.split('-')[0], 10);
                  const max = parseInt(priceQuery.value.split('-')[1], 10);

                  facet.addSalesPriceRangeFacet('Product', min, max, { includeZeroHitsInAvailable: true });
                } else {
                  facet.addSalesPriceRangeFacet('Product');
                }

                if (saleQuery) {
                  facet.addProductDataBooleanValueFacet(`${window.Shopify.shop}_IsOnSale`, 'Product', [true]);
                }

                break;
              default:
                facet.addProductDataStringValueFacet(group, 'Product', [...selectedGroupValues], undefined, { includeZeroHitsInAvailable: true });
                break;
            }
          });
        }
      })

      /**
       * Sort the response so unavailable products are last
       */
      .sorting((s) => {
        const sortValue = queries.find((query) => query.key === 'sort_by')?.value || defaultSort;

        switch (sortValue) {
          case 'bestselling':
            s.sortByProductPopularity();
            break;

          case 'created-at-descending':
            s.sortByProductData(
              `${window.Shopify.shop}_CreatedAt`,
              'Product',
              'Descending',
              (n) => n.sortByProductRelevance(),
            );
            break;

          case 'price-ascending':
            s.sortByProductAttribute(
              'SalesPrice',
              'Ascending',
              (n) => n.sortByProductRelevance(),
            );
            break;

          case 'price-descending':
            s.sortByProductAttribute(
              'SalesPrice',
              'Descending',
              (n) => n.sortByProductRelevance(),
            );
            break;

          case 'title-ascending':
            s.sortByProductAttribute(
              'DisplayName',
              'Ascending',
              (n) => n.sortByProductRelevance(),
            );
            break;

          case 'title-descending':
            s.sortByProductAttribute(
              'DisplayName',
              'Descending',
              (n) => n.sortByProductRelevance(),
            );
            break;

          default:
            s.sortByProductData(
              'InStock',
              'Product',
              'Descending',
              (n) => n.sortByProductRelevance(),
            );
            break;
        }
      });

    return this.middleman.searcher.searchProducts(search.build());
  }
}

export default Collection;
